import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { POI_TYPE_LABELS, type PoiType } from '@camper-diary/shared';
import { listPois, type Poi } from '../api/pois';

type Props = {
  /** Chiamato quando l'utente seleziona un POI: il chiamante riempie titolo/lat/lon/address. */
  onPick: (poi: Poi) => void;
};

function countryToFlag(code: string | null): string {
  if (!code || code.length !== 2) return '🌍';
  const cc = code.toUpperCase();
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  const a = cc.charCodeAt(0);
  const b = cc.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return '🌍';
  return String.fromCodePoint(base + a, base + b);
}

/**
 * Pulsante "📍 Da POI" + dropdown con la lista dei POI salvati, raggruppati
 * per stato/regione e filtrabili per tipo. Lazy-load al primo open.
 */
export function PoiPicker({ onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [pois, setPois] = useState<Poi[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || pois !== null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listPois()
      .then((list) => {
        if (!cancelled) setPois(list);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore caricamento');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pois]);

  const filtered = useMemo(() => {
    if (!pois) return [];
    const q = search.trim().toLowerCase();
    if (!q) return pois;
    return pois.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.address ?? '').toLowerCase().includes(q) ||
        (p.region ?? '').toLowerCase().includes(q),
    );
  }, [pois, search]);

  const groups = useMemo(() => {
    type Group = { country: string | null; regions: { region: string | null; pois: Poi[] }[] };
    const byCountry = new Map<string, Group>();
    for (const p of filtered) {
      const cKey = p.country ?? '__';
      if (!byCountry.has(cKey)) {
        byCountry.set(cKey, { country: p.country, regions: [] });
      }
      const g = byCountry.get(cKey)!;
      let r = g.regions.find((x) => x.region === p.region);
      if (!r) {
        r = { region: p.region, pois: [] };
        g.regions.push(r);
      }
      r.pois.push(p);
    }
    const list = [...byCountry.values()];
    list.sort((a, b) => {
      if (a.country === null) return 1;
      if (b.country === null) return -1;
      if (a.country === 'IT' && b.country !== 'IT') return -1;
      if (b.country === 'IT' && a.country !== 'IT') return 1;
      return a.country.localeCompare(b.country);
    });
    for (const g of list) {
      g.regions.sort((a, b) => {
        if (a.region === null) return 1;
        if (b.region === null) return -1;
        return a.region.localeCompare(b.region);
      });
      for (const r of g.regions) {
        r.pois.sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    return list;
  }, [filtered]);

  function pick(p: Poi) {
    onPick(p);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="poi-picker">
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        📍 Da POI
      </button>
      {open && (
        <div
          className="picker-dropdown poi-picker-dropdown"
          onMouseDown={(e) => e.preventDefault() /* evita blur su input parent */}
        >
          {loading && <p className="muted">Caricamento…</p>}
          {error && <p className="err">{error}</p>}
          {pois && pois.length === 0 && (
            <p className="muted">
              Nessun POI salvato.{' '}
              <Link to="/pois" onClick={() => setOpen(false)}>
                Aggiungine uno
              </Link>
              .
            </p>
          )}
          {pois && pois.length > 0 && (
            <>
              <input
                type="text"
                className="poi-picker-search"
                placeholder="Cerca per nome, indirizzo o regione…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              {filtered.length === 0 && (
                <p className="muted">Nessun POI corrisponde alla ricerca.</p>
              )}
              <div className="poi-picker-groups">
                {groups.map((g) => (
                  <div key={g.country ?? '__'} className="poi-picker-country">
                    <div className="poi-picker-country-head">
                      <span>{countryToFlag(g.country)}</span>
                      <strong>{g.country ?? 'Senza paese'}</strong>
                    </div>
                    {g.regions.map((r) => (
                      <div key={r.region ?? '__'} className="poi-picker-region">
                        <div className="poi-picker-region-head">
                          {r.region ?? '(regione non rilevata)'}
                        </div>
                        <ul className="picker-list">
                          {r.pois.map((p) => {
                            const meta = POI_TYPE_LABELS[p.type as PoiType];
                            return (
                              <li key={p.id}>
                                <button
                                  type="button"
                                  className="picker-row"
                                  onClick={() => pick(p)}
                                >
                                  <span className="picker-label">
                                    <span className="poi-picker-type">{meta.icon}</span>{' '}
                                    {p.name}
                                  </span>
                                  {p.address && (
                                    <span className="picker-address">{p.address}</span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
